/**
 * Real-Time Data Service
 * Provides real-time data subscriptions using Firestore onSnapshot
 * Replaces getDocs with onSnapshot for live updates
 */

import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { firebaseLogger } from '../utils/logger';

/**
 * Active subscriptions map to prevent duplicate subscriptions
 */
const activeSubscriptions = new Map();

/**
 * Subscribe to a collection with real-time updates
 * @param {string} collectionName - Collection name
 * @param {Function} callback - Callback function (data, error) => void
 * @param {Object} options - Query options { where, orderBy, limit }
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToCollection = (collectionName, callback, options = {}) => {
  const {
    whereConditions = [],
    orderByField = null,
    orderDirection = 'desc',
    limitCount = null,
    transform = null
  } = options;

  try {
    const subscriptionKey = `${collectionName}-${JSON.stringify(options)}`;
    
    // If subscription already exists, return existing unsubscribe
    if (activeSubscriptions.has(subscriptionKey)) {
      firebaseLogger.warn(`Subscription already exists for ${subscriptionKey}`);
      return activeSubscriptions.get(subscriptionKey);
    }

    let q = collection(db, collectionName);

    // Apply where conditions
    whereConditions.forEach(condition => {
      q = query(q, where(condition.field, condition.operator, condition.value));
    });

    // Apply orderBy
    if (orderByField) {
      q = query(q, orderBy(orderByField, orderDirection));
    }

    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    // Create snapshot listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Apply transform if provided
          const transformedData = transform ? transform(data) : data;

          callback(transformedData, null);
        } catch (error) {
          firebaseLogger.error(`Error processing snapshot for ${collectionName}:`, error);
          callback(null, error);
        }
      },
      (error) => {
        firebaseLogger.error(`Snapshot error for ${collectionName}:`, error);
        callback(null, error);
      }
    );

    // Store subscription
    activeSubscriptions.set(subscriptionKey, unsubscribe);

    // Return unsubscribe function that also cleans up
    return () => {
      unsubscribe();
      activeSubscriptions.delete(subscriptionKey);
    };
  } catch (error) {
    firebaseLogger.error(`Error creating subscription for ${collectionName}:`, error);
    callback(null, error);
    return () => {}; // Return no-op unsubscribe
  }
};

/**
 * Subscribe to a single document with real-time updates
 * @param {string} collectionName - Collection name
 * @param {string} documentId - Document ID
 * @param {Function} callback - Callback function (data, error) => void
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToDocument = (collectionName, documentId, callback) => {
  try {
    const subscriptionKey = `${collectionName}/${documentId}`;
    
    // If subscription already exists, return existing unsubscribe
    if (activeSubscriptions.has(subscriptionKey)) {
      firebaseLogger.warn(`Subscription already exists for ${subscriptionKey}`);
      return activeSubscriptions.get(subscriptionKey);
    }

    const docRef = doc(db, collectionName, documentId);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          callback({
            id: snapshot.id,
            ...snapshot.data()
          }, null);
        } else {
          callback(null, new Error('Document does not exist'));
        }
      },
      (error) => {
        firebaseLogger.error(`Snapshot error for ${collectionName}/${documentId}:`, error);
        callback(null, error);
      }
    );

    activeSubscriptions.set(subscriptionKey, unsubscribe);

    return () => {
      unsubscribe();
      activeSubscriptions.delete(subscriptionKey);
    };
  } catch (error) {
    firebaseLogger.error(`Error creating document subscription:`, error);
    callback(null, error);
    return () => {};
  }
};

/**
 * Subscribe to a query with date range filtering
 * @param {string} collectionName - Collection name
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} dateField - Date field name (default: 'created_at' or 'createdAt')
 * @param {Function} callback - Callback function
 * @param {Object} options - Additional options
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToDateRange = (
  collectionName,
  startDate,
  endDate,
  dateField = null,
  callback,
  options = {}
) => {
  const {
    orderByField = null,
    orderDirection = 'desc',
    limitCount = null,
    transform = null
  } = options;

  try {
    // Determine date field name (check both snake_case and camelCase)
    const fieldName = dateField || 'created_at';
    const altFieldName = dateField || 'createdAt';

    const whereConditions = [];
    
    if (startDate) {
      whereConditions.push({
        field: fieldName,
        operator: '>=',
        value: Timestamp.fromDate(startDate)
      });
    }
    
    if (endDate) {
      whereConditions.push({
        field: fieldName,
        operator: '<=',
        value: Timestamp.fromDate(endDate)
      });
    }

    return subscribeToCollection(
      collectionName,
      callback,
      {
        whereConditions,
        orderByField: orderByField || fieldName,
        orderDirection,
        limitCount,
        transform
      }
    );
  } catch (error) {
    firebaseLogger.error(`Error creating date range subscription:`, error);
    callback(null, error);
    return () => {};
  }
};

/**
 * Subscribe to multiple collections simultaneously
 * @param {Array} subscriptions - Array of { collectionName, callback, options }
 * @returns {Function} - Unsubscribe function for all subscriptions
 */
export const subscribeToMultiple = (subscriptions) => {
  const unsubscribes = subscriptions.map(sub =>
    subscribeToCollection(sub.collectionName, sub.callback, sub.options || {})
  );

  return () => {
    unsubscribes.forEach(unsubscribe => unsubscribe());
  };
};

/**
 * Get current data once (non-real-time, for initial load)
 * @param {string} collectionName - Collection name
 * @param {Object} options - Query options
 * @returns {Promise<Array>} - Array of documents
 */
export const getCollectionOnce = async (collectionName, options = {}) => {
  const {
    whereConditions = [],
    orderByField = null,
    orderDirection = 'desc',
    limitCount = null
  } = options;

  try {
    const { getDocs } = await import('firebase/firestore');
    
    let q = collection(db, collectionName);

    whereConditions.forEach(condition => {
      q = query(q, where(condition.field, condition.operator, condition.value));
    });

    if (orderByField) {
      q = query(q, orderBy(orderByField, orderDirection));
    }

    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    firebaseLogger.error(`Error getting collection ${collectionName}:`, error);
    throw error;
  }
};

/**
 * Clean up all active subscriptions
 */
export const cleanupAllSubscriptions = () => {
  activeSubscriptions.forEach((unsubscribe, key) => {
    firebaseLogger.debug(`Cleaning up subscription: ${key}`);
    unsubscribe();
  });
  activeSubscriptions.clear();
};

/**
 * Get count of active subscriptions (for debugging)
 * @returns {number} - Number of active subscriptions
 */
export const getActiveSubscriptionCount = () => {
  return activeSubscriptions.size;
};

/**
 * Check if a subscription exists
 * @param {string} subscriptionKey - Subscription key
 * @returns {boolean} - Whether subscription exists
 */
export const hasSubscription = (subscriptionKey) => {
  return activeSubscriptions.has(subscriptionKey);
};




